import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import { ContextOperation } from "../../../context/OperationContext";
import useOperationTranslation from "../../../hooks/useOperationTranslation";
import useSnackbar from "../../../hooks/useSnackbar";
import AutoComplete, { AutoCompleteOption } from "../../common/AutoComplete";
import Ellipsis from "../../common/Ellipsis";
import { nameof } from "../../common/ObjectUtils";
import EquipmentAssociationService, { EquipmentAssociationRequests } from "../EquipmentAssociationService";
import { EquipmentAssociationTypes } from "../Types";
import { getAllKits } from "../Utils";

interface Props {
  tenant:string;
  open: boolean;
  onClose: () => void;
  acris?: EquipmentAssociationTypes.ACRISS;
  updateDatasourceKit: () => void;
  updateDatasourceAcriss: () => void;
}

const ManageEquipmentKits = (props: Props) => {
  
  const [equipmentKits, setEquipmentKits] = useState<AutoCompleteOption[]>([]);

  const snackbar = useSnackbar();
  const t = useOperationTranslation();

  const onClose = () => {
    formik.resetForm();
    props.onClose();
  }

  useEffect(() => {
      getAllKits(props.tenant).then(
        response => setEquipmentKits(response),
    )
  }, [props.tenant]);

  useEffect(() => {
    if (!props.acris || !props.open) return;

    formik.setFieldValue(nameof<EquipmentAssociationRequests.Edit>("ids"), [props.acris.id]);

    EquipmentAssociationService.get.acrissKitsById(props.tenant,props.acris.id).then(
      response => {
        formik.setFieldValue(nameof<EquipmentAssociationRequests.Edit>("equipmentKitsId"), response.map((kit) => kit.id))
      },
    )
  }, [props.open]);

  const handleSubmit = (data: EquipmentAssociationRequests.Edit) => {
    EquipmentAssociationService.put.edit(props.tenant,data).then(
      () => {
        onClose();
        props.updateDatasourceKit();
        snackbar.show("success", t("vehicleEquipmentAssociation.dialogs.editKits.messages.success"));
      },
      () => {
        onClose();
        snackbar.show("error", t("vehicleEquipmentAssociation.dialogs.editKits.messages.error"));
      }
    )
  }

  const formik = useFormik<EquipmentAssociationRequests.Edit>({
    initialValues: {
      equipmentKitsId: [],
      ids: [],
    },
    onSubmit: handleSubmit,
  })

  return (
    <Mui.Dialog open={props.open} onClose={onClose} fullWidth={true}>
      <Mui.DialogTitle>
        <Mui.Grid container alignItems="center" wrap="nowrap">
          <Mui.Grid item xs={11}>
            <Mui.Stack direction="row" alignItems="center" gap="5px" width="100%" flexWrap="wrap">
              <Mui.Typography variant="bodyLarge">{t("vehicleEquipmentAssociation.dialogs.editKits.title")}</Mui.Typography>

              <Mui.Typography variant="h5" width="100%">
                <Mui.Tooltip title={`${props.acris?.code} - ${props.acris?.label}`}>
                  <Ellipsis>
                    {`${props.acris?.code} - ${props.acris?.label}`}
                  </Ellipsis>
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Stack>
          </Mui.Grid>

          <Mui.Grid item xs="auto" ml="auto">
            <Mui.IconButton onClick={onClose}>
              <MuiIcons.Close />
            </Mui.IconButton>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.DialogTitle>

      <Mui.DialogContent>
        <form id="manage-equipments-acriss-form" onSubmit={formik.handleSubmit}>
          <AutoComplete
            options={equipmentKits}
            label={t("vehicleEquipmentAssociation.dialogs.editKits.form.equipmentKit")}
            fullWidth={true}
            multiple={true}
            onChange={(value) => formik.setFieldValue(nameof<EquipmentAssociationRequests.Edit>("equipmentKitsId"), value)}
            value={formik.values.equipmentKitsId}
          />
        </form>
      </Mui.DialogContent>

      <Mui.DialogActions>
        <Mui.Stack direction="row" justifyContent="end" gap="16px">
          <Mui.Button variant="outlined" color="accent" onClick={onClose}>
            {t("vehicleEquipmentAssociation.dialogs.editKits.actions.cancel")}
          </Mui.Button>

          <Mui.Button color="accent" onClick={formik.submitForm}>
            {t("vehicleEquipmentAssociation.dialogs.editKits.actions.save")}
          </Mui.Button>
        </Mui.Stack>
      </Mui.DialogActions>
    </Mui.Dialog>
  )
}

export default ManageEquipmentKits;